import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';

import patchCartItem from '@/common/api/carts/items/patchCartItem';
import putCartItems from '@/common/api/carts/items/putCartItems';
import patchCart from '@/common/api/carts/patchCart';
import patchToggleSelection from '@/common/api/carts/patchToggleSelection';
import putEmptyCart from '@/common/api/carts/putEmptyCart';
import Snack from '@/common/components/molecules/Snack/Snack';
import { ROUTE_SCANBAT } from '@/common/constants';
import { useDefaultCartContext } from '@/common/contexts/DefaultCartContext';
import { useModalContext } from '@/common/contexts/ModalContext';
import { useRemoveFromContext } from '@/common/contexts/RemoveFromContext';
import patchMoveCartItems from '@/ebat/api/carts/items/patchMoveCartItems';
import postCart from '@/ebat/api/carts/postCart';

// All methods for cart page:
// - toggleSelect
// - toggleSelectAll
// - modify cart item (quantity, selected)
// - delete cart item
// All attributes for cart page
// - allIsSelected
// - isUpdate
// - test commit
const useManageCarts = ({
  cart,
  disabled,
  isLoading,
  isSuccess,
  refetchCart,
}) => {
  const { t } = useTranslation();
  const [localCart, setLocalCart] = useState(cart);
  const { refetchDefaultCart } = useDefaultCartContext();
  const { setModalIsOpen } = useModalContext();
  const CEIL_TOLERANCE = 0.000001;

  const isScanbat = window.location.pathname.includes(ROUTE_SCANBAT);

  const {
    removeFromCart,
    isLoading: isLoadingDeleteCartItem,
    isSuccess: isSuccessDeleteCartItem,
  } = useRemoveFromContext();

  // create a handler to refresh default cart info
  useEffect(() => {
    if (!isLoading && !isScanbat) {
      refetchDefaultCart();
    }
  }, [isLoading, isScanbat, refetchDefaultCart]);

  // Use effect generate a side effect when cart is changing
  useEffect(() => {
    setLocalCart(cart);
  }, [cart]);

  // SELECT ALL
  // determine action to do (selecting/unselecting) and make a call on the endpoint to update items
  // Mutator for toggle select cart items
  const {
    mutate: mutatePatchToggleSelection,
    isLoading: isLoadingToggleSelection,
    isSuccess: isSuccessToggleSelection,
  } = useMutation(patchToggleSelection);
  // Toggle all callback
  const onToggleSelectAll = useCallback(() => {
    // get items from local cart in state
    const { items } = localCart;

    const selectedItems = items.every((item) => {
      if (!item.diff) {
        // Si l'article n'est pas diff, il doit être sélectionné
        return item.is_selected;
      }
      // Si c'est un article diff, au moins un des deux articles diff doit être sélectionné
      const pairItem = items.find((otherItem) => otherItem.sku === item.diff);
      return item.is_selected || (pairItem && pairItem.is_selected);
    });

    // set default action for the endpoint
    let action = false;
    // calculate the action to do (select/deselect)
    if (selectedItems) {
      action = false;
    } else {
      action = true;
    }

    // mutation callback
    mutatePatchToggleSelection(
      {
        id: localCart.id,
        select: action,
      },
      {
        onSuccess: (data) => {
          // Update local cart items (state)
          setLocalCart({
            ...data,
          });

          // Display success flashMessage
          toast.success(
            <Snack message={t('common:messages:forms.success.default')} />,
          );
        },
        onError: (error) => {
          // In case of error
          if (error.response.status === 500) {
            toast.error(
              <Snack
                message={t('common:messages:forms.errors.server_error')}
              />,
            );
          } else {
            toast.error(
              <Snack message={t('common:messages:forms.errors.default')} />,
            );
          }
        },
      },
    );
  }, [localCart, mutatePatchToggleSelection, t]);

  // ALL IS SELECTED
  // calculate if all cart items are selected
  const allIsSelected = useCallback(() => {
    const { items: stateList } = localCart;

    const selected = stateList.every((item) => {
      if (!item.diff) {
        // Si l'article n'est pas diff, il doit être sélectionné
        return item.is_selected;
      }
      // Si c'est un article diff, au moins un des deux articles diff doit être sélectionné
      const pairItem = stateList.find(
        (otherItem) => otherItem.sku === item.diff,
      );
      return item.is_selected || (pairItem && pairItem.is_selected);
    });

    return selected;
  }, [localCart]);

  // IS UPDATE
  // Calculate if local cart are different from the given cart at the first page loading.
  const isUpdate = useCallback(() => {
    let diff = 0;

    if (!localCart) {
      return true;
    }
    /* eslint-disable react/prop-types*/
    const { items: propsList } = cart;
    const { items: stateList } = localCart;
    // eslint-disable-next-line react/prop-types
    if (propsList.length !== stateList.length) {
      return true;
    }

    if (cart.subtotal !== localCart.subtotal) {
      return true;
    }
    // eslint-disable-next-line react/prop-types
    propsList.forEach((item) => {
      const index = stateList.findIndex(
        (i) =>
          i.id === item.id &&
          i.quantity === item.quantity &&
          i.is_selected === item.is_selected,
      );
      if (index < 0) {
        diff += 1;
      }
    });
    return diff > 0;
  }, [cart, localCart]);

  // MODIFY
  // Modify a cart idem (quantity and select)
  // Mutator
  const {
    mutate: mutatePatchCartItem,
    isLoading: isLoadingUpdateCartItem,
    isSuccess: isSuccessUpdateCartItem,
  } = useMutation(patchCartItem);
  // mutation callback
  // update the cart idem with new quantity or select
  const onUpdateCartItem = useCallback(
    ({ id, isSelected, isSelectedDiff, quantity, onSuccess }) => {
      mutatePatchCartItem(
        {
          cartId: localCart.id,
          id,
          is_selected: isSelected,
          is_diff_selected: isSelectedDiff,
          quantity,
        },
        {
          onSuccess: (data) => {
            if (onSuccess) {
              onSuccess();
            } else {
              // Update local cart items (state)
              setLocalCart({
                ...localCart,
                items: localCart.items.map((item) => {
                  if (item.id === id) {
                    return { ...item, ...data };
                  }

                  return item;
                }),
              });

              // Display success flashMessage
              toast.success(
                <Snack message={t('common:messages:forms.success.default')} />,
              );
            }
          },
          onError: (error) => {
            // In case of error
            if (error.response.status === 500) {
              toast.error(
                <Snack
                  message={t('common:messages:forms.errors.server_error')}
                />,
              );
            } else {
              toast.error(
                <Snack message={t('common:messages:forms.errors.default')} />,
              );
            }
          },
        },
      );
    },
    [localCart, mutatePatchCartItem, t],
  );

  const onUpdateLocalCartItem = useCallback(
    ({ id, isSelected, isSelectedDiff, quantity }) => {
      setLocalCart({
        ...localCart,
        items: localCart.items.map((item) => {
          if (item.id === id) {
            return {
              ...item,
              id,
              is_selected: isSelected,
              is_diff_selected: isSelectedDiff,
              quantity,
            };
          }

          return item;
        }),
      });
    },
    [localCart],
  );

  const {
    mutate: mutatePutCartItems,
    isLoading: isLoadingPutCartItems,
    isSuccess: isSuccessPutCartItems,
  } = useMutation(putCartItems);
  /**
   * Callback given to children component to put cart items
   * @param {function} onSuccess
   */
  const onPutCartItems = useCallback(
    ({ items, onSuccess = () => {} }) => {
      const itemsToMap = items || localCart.items;
      mutatePutCartItems(
        {
          // TODO: Solution temporaire avant le refacto des paniers
          items: itemsToMap.map((item) => {
            const salesPackaging =
              item.product?.price_and_stock?.sales_packaging;
            const splitPackaging =
              item.product?.price_and_stock?.split_packaging;

            return {
              id: item.id,
              sku: item.sku,
              quantity:
                salesPackaging && !splitPackaging
                  ? Math.ceil(item.quantity / salesPackaging - CEIL_TOLERANCE) *
                    salesPackaging
                  : item.quantity,
              is_selected: item.is_selected,
              is_diff_selected: item.is_diff_selected || false,
            };
          }),
          cart: { ...localCart },
        },
        {
          onSuccess: () => {
            onSuccess();
            refetchCart();
          },
          onError: (error) => {
            if (error.response.status === 500) {
              toast.error(
                <Snack
                  message={t('common:messages:forms.errors.server_error')}
                />,
              );
            } else {
              toast.error(
                <Snack message={t('common:messages:forms.errors.default')} />,
              );
            }
          },
        },
      );
    },
    [localCart, mutatePutCartItems, refetchCart, t],
  );

  /**
   * Delete an item in cart and update the local cart items
   * @param {object} cartItem { id: int } item id in the cart
   */
  const onDeleteCartItem = useCallback(
    ({ id, onSuccess }) => {
      removeFromCart({
        cart: { ...localCart },
        item: { id },
        onSuccessCallback: () =>
          onSuccess
            ? onSuccess()
            : setLocalCart({
                ...localCart,
                items: localCart.items.filter((item) => item.id !== id),
              }),
      });
    },
    [localCart, removeFromCart],
  );

  /**
   * Update cart mutator
   */
  const {
    mutate: mutatePatchCart,
    isLoading: isLoadingPatchCart,
    isSuccess: isSuccessPatchCart,
  } = useMutation(patchCart);

  /**
   * Update cart to change his name, address or worksite
   * @param {object} cartToUpdate
   * @param {function} onError
   * @param {function} onSuccess
   */
  const onUpdateCart = useCallback(
    ({ cartToUpdate, onError = () => {}, onSuccess = () => {} }) => {
      mutatePatchCart(
        {
          id: localCart.id,
          name: cartToUpdate.name,
          address_id: cartToUpdate.address_id,
          worksite_code: cartToUpdate.worksite_code,
        },
        {
          onSuccess: (data) => {
            onSuccess(data);
            refetchCart();
            setModalIsOpen(false);

            toast.success(
              <Snack message={t('common:messages:forms.success.default')} />,
            );
          },
          onError: (error) => {
            onError(error);

            if (error.response.status === 500) {
              toast.error(
                <Snack
                  message={t('common:messages:forms.errors.server_error')}
                />,
              );
            } else {
              toast.error(
                <Snack message={t('common:messages:forms.errors.default')} />,
              );
            }
          },
        },
      );
    },
    [t, localCart, refetchCart, setModalIsOpen, mutatePatchCart],
  );

  const {
    mutate: mutatePutEmptyCart,
    isLoading: isLoadingPutEmptyCart,
    isSuccess: isSuccessPutEmptyCart,
  } = useMutation(putEmptyCart);

  const onEmptyCart = useCallback(
    ({ cart: cartToEmpty, onSuccess }) => {
      mutatePutEmptyCart(
        {
          id: cartToEmpty.id,
        },
        {
          onSuccess: () => {
            toast.success(
              <Snack message={t('common:messages:forms.success.empty')} />,
            );
            onSuccess();
          },
          onError: (error) => {
            if (error.response.status === 500) {
              toast.error(
                <Snack
                  message={t('common:messages:forms.errors.server_error')}
                />,
              );
            } else {
              toast.error(
                <Snack message={t('common:messages:forms.errors.default')} />,
              );
            }
          },
        },
      );
    },
    [t, mutatePutEmptyCart],
  );

  const {
    mutate: mutatePatchMoveCartItems,
    isLoading: isLoadingPatchMoveCartItems,
    isSuccess: isSuccessPatchMoveCartItems,
  } = useMutation(patchMoveCartItems);

  const onMoveCartItems = useCallback(
    ({ target, onSuccess }) => {
      const selectedItems = localCart?.items
        .filter((item) => item.is_selected)
        .reduce((acc, item) => {
          acc.push({ id: item.id });
          return acc;
        }, []);

      if (selectedItems.length > 0) {
        mutatePatchMoveCartItems(
          {
            cartId: localCart.id,
            targetCartId: target.id,
            items: selectedItems,
          },
          {
            onSuccess: () => {
              setModalIsOpen(false);
              onSuccess();

              toast.success(
                <Snack message={t('common:messages:forms.success.default')} />,
              );
            },
            onError: (error) => {
              if (error.response.status === 500) {
                toast.error(
                  <Snack
                    message={t('common:messages:forms.errors.server_error')}
                  />,
                );
              } else {
                toast.error(
                  <Snack message={t('common:messages:forms.errors.default')} />,
                );
              }
            },
          },
        );
      } else {
        setModalIsOpen(false);
      }
    },
    [localCart, mutatePatchMoveCartItems, setModalIsOpen, t],
  );

  const { mutate: mutatePostCart, isLoading: isLoadingPostCart } = useMutation(
    postCart,
  );

  const onPostCart = useCallback(
    ({ values, actions, onSuccess = () => {} }) => {
      mutatePostCart(
        { ...values },
        {
          onSuccess: (data) => {
            const { id } = data;

            onMoveCartItems({ target: { id }, onSuccess: refetchCart });
            onSuccess(id);
          },
          onError: (error) => {
            if (error.response.status === 500) {
              toast.error(
                <Snack
                  message={t('common:messages:forms.errors.server_error')}
                />,
              );
            } else {
              if (error.response.data.errors) {
                actions.setErrors(error.response.data.errors);
              }

              toast.error(
                <Snack message={t('common:messages:forms.errors.default')} />,
              );
            }
          },
        },
      );
    },
    [mutatePostCart, onMoveCartItems, refetchCart, t],
  );

  /**
   * Create disabled object
   */
  const isDisabled = useMemo(() => {
    const loading =
      isLoading ||
      isLoadingDeleteCartItem ||
      isLoadingPatchCart ||
      isLoadingPatchMoveCartItems ||
      isLoadingPostCart ||
      isLoadingPutCartItems ||
      isLoadingPutEmptyCart ||
      isLoadingToggleSelection ||
      isLoadingUpdateCartItem;
    const blocked = loading || disabled;

    return {
      loading,
      disabled: blocked,
    };
  }, [
    disabled,
    isLoading,
    isLoadingDeleteCartItem,
    isLoadingPatchCart,
    isLoadingPatchMoveCartItems,
    isLoadingPostCart,
    isLoadingPutCartItems,
    isLoadingPutEmptyCart,
    isLoadingToggleSelection,
    isLoadingUpdateCartItem,
  ]);

  /**
   * Get isSuccess value
   */
  const getIsSuccess = useMemo(
    () =>
      isSuccess ||
      isSuccessDeleteCartItem ||
      isSuccessPatchCart ||
      isSuccessPatchMoveCartItems ||
      isSuccessPutCartItems ||
      isSuccessPutEmptyCart ||
      isSuccessToggleSelection ||
      isSuccessUpdateCartItem,
    [
      isSuccess,
      isSuccessDeleteCartItem,
      isSuccessPatchCart,
      isSuccessPatchMoveCartItems,
      isSuccessPutCartItems,
      isSuccessPutEmptyCart,
      isSuccessToggleSelection,
      isSuccessUpdateCartItem,
    ],
  );

  return {
    allIsSelected,
    isDisabled,
    isSuccess: getIsSuccess,
    isUpdate,
    localCart,
    onDeleteCartItem,
    onEmptyCart,
    onMoveCartItems,
    onPostCart,
    onPutCartItems,
    onToggleSelectAll,
    onUpdateCart,
    onUpdateCartItem,
    onUpdateLocalCartItem,
    setLocalCart,
  };
};

export default useManageCarts;
